import React, { useCallback, useEffect, useMemo } from 'react'
import { Box, Button, CircularProgress, Grid, makeStyles, Typography } from '@material-ui/core'
import { useDispatch, useSelector } from 'react-redux'

import conferenceSlice, { fetchConferenceById, resetConferenceStore } from 'store/conference'
import { Roles } from 'constants/index'
import { getDefaultTimezone } from 'utils/times'
import { useHistory, useParams } from 'react-router-dom'

// Import modular components
import BasicInformationForm from 'views/admin/conference/components/BasicInformationForm'
import FeatureTogglesForm from 'views/admin/conference/components/FeatureTogglesForm'
import ResourcesForm from 'views/admin/conference/components/ResourcesForm'
import EducationCreditsForm from 'views/admin/conference/components/EducationCreditsForm'
import ConferenceImagesForm from 'views/admin/conference/components/ConferenceImagesForm'
import TagsManagementForm from 'views/admin/conference/components/TagsManagementForm'
import ConferenceAdminsForm from 'views/admin/conference/components/ConferenceAdminsForm'

const ModularConferenceForm = () => {
  const classes = useStyles()
  const dispatch = useDispatch()
  const history = useHistory()
  const { conferenceId } = useParams()
  const { user } = useSelector((state) => state.auth)
  const { conference, conferences, inProgress } = useSelector((state) => state.conference)

  const defaultTimezone = useMemo(() => getDefaultTimezone(conference?.timezone), [conference])

  const previousConfs = useCallback(() => {
    if (conferenceId) {
      return conferences.filter((conf) => conf.id !== conferenceId)
    }
    return conferences
  }, [conferences, conferenceId])

  useEffect(() => {
    if (conferenceId) {
      dispatch(fetchConferenceById(conferenceId))
    }
    return () => {
      if (user?.role === Roles.SUPER_ADMIN) {
        dispatch(conferenceSlice.actions.setConference(null))
      }
    }
  }, [dispatch, conferenceId, user?.role])

  const goBack = () => {
    dispatch(resetConferenceStore())
    history.goBack()
  }

  return (
    <Box className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h5" className={classes.pageTitle}>
          Edit Conference
        </Typography>
        {user.role === Roles.SUPER_ADMIN && (
          <Button variant="outlined" onClick={goBack}>
            Back
          </Button>
        )}
      </Box>

      {inProgress ? (
        <Box className={classes.loadingContainer}>
          <CircularProgress />
          <Typography>Loading, Please wait...</Typography>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {/* Left Column */}
          <Grid item xs={12} md={7}>
            <BasicInformationForm
              conference={conference}
              conferences={conferences}
              defaultTimezone={defaultTimezone}
              user={user}
            />

            <ResourcesForm conference={conference} />

            <EducationCreditsForm conference={conference} />

            <TagsManagementForm conference={conference} />

            <ConferenceAdminsForm conference={conference} user={user} />
          </Grid>

          {/* Right Column */}
          <Grid item xs={12} md={5}>
            <FeatureTogglesForm
              conference={conference}
              conferences={conferences}
              user={user}
              previousConfs={previousConfs}
            />

            <ConferenceImagesForm conference={conference} />
          </Grid>
        </Grid>
      )}
    </Box>
  )
}

const useStyles = makeStyles({
  container: {
    width: '100%',
    padding: '0',
    boxSizing: 'border-box'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '10px 0'
  },
  pageTitle: {
    fontWeight: 600,
    fontSize: '26px'
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '200px',
    gap: '1rem'
  },
  section: {
    marginBottom: '24px',
    padding: '18px 24px',
    borderRadius: '8px',
    boxShadow: 'rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px',
    backgroundColor: '#ffffff'
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: '20px',
    borderBottom: '1px solid #eee',
    paddingBottom: '10px'
  },
  subheading: {
    fontWeight: 600
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: '24px',
    gap: '12px'
  },
  cancelButton: {
    marginRight: '12px'
  }
})

export default ModularConferenceForm
